const currencySymbols = {
  JPY: "¥",
  USD: "$",
  EUR: "€",
  GBP: "£",
  CNY: "¥",
  KRW: "₩",
};

const checklistGroups = [
  {
    key: "required",
    label: "必須",
    keywords: ["必須", "貴重品", "書類", "財布", "パスポート", "チケット"],
  },
  {
    key: "clothes",
    label: "衣類",
    keywords: ["衣類", "服", "着替え", "下着", "靴", "帽子", "タオル"],
  },
  {
    key: "gadgets",
    label: "ガジェット",
    keywords: [
      "ガジェット",
      "スマホ",
      "携帯",
      "充電",
      "カメラ",
      "電源",
      "バッテリー",
      "pc",
    ],
  },
  { key: "other", label: "その他", keywords: [] },
];

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/\n/g, "<br />");
}

function formatDate(value, includeYear = true) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return escapeHtml(value);
  }

  const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
  const prefix = includeYear ? `${date.getFullYear()}年` : "";
  return `${prefix}${date.getMonth() + 1}月${date.getDate()}日（${
    weekdays[date.getDay()]
  }）`;
}

function formatGeneratedAt(value = new Date()) {
  const date = new Date(value);
  return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}/${String(date.getDate()).padStart(2, "0")} ${String(
    date.getHours(),
  ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function formatMoney(amount, currency = "JPY") {
  const numericAmount = Number(amount ?? 0);
  if (!Number.isFinite(numericAmount)) return "-";

  const symbol = currencySymbols[currency] || `${currency} `;
  return `${symbol}${numericAmount.toLocaleString("ja-JP")}`;
}

function formatTotalCost(costs) {
  if (!costs || Object.keys(costs).length === 0) return "-";

  return Object.entries(costs)
    .map(([currency, amount]) => formatMoney(amount, currency))
    .join(" / ");
}

function parseDestinations(shiori) {
  if (!shiori?.DESTINATIONS) return "-";

  try {
    const destinations = JSON.parse(shiori.DESTINATIONS);
    return Array.isArray(destinations) && destinations.length > 0
      ? destinations.join("、")
      : "-";
  } catch {
    return shiori.DESTINATIONS || "-";
  }
}

function buildAddress(schedule) {
  return [
    schedule.PREFECTURE,
    schedule.CITY,
    schedule.TOWN,
    schedule.ADDRESS_NMV,
    schedule.BUILDING,
  ]
    .filter(Boolean)
    .join("");
}

function buildPhone(schedule) {
  return [
    schedule.TEL_AREA_CODE,
    schedule.TEL_CITY_CODE,
    schedule.TEL_SUBSCRIBER_NUMBER,
  ]
    .filter(Boolean)
    .join("-");
}

function buildMapUrl(schedule) {
  const latitude = Number(schedule.LATITUDE);
  const longitude = Number(schedule.LONGITUDE);

  if (
    schedule.LATITUDE !== null &&
    schedule.LATITUDE !== undefined &&
    schedule.LATITUDE !== "" &&
    schedule.LONGITUDE !== null &&
    schedule.LONGITUDE !== undefined &&
    schedule.LONGITUDE !== "" &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude)
  ) {
    return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
  }

  const address = buildAddress(schedule);
  return address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address,
      )}`
    : "";
}

function getReservationText(schedule) {
  if (schedule.NEED_FLG === "0") return "予約不要";
  if (schedule.RESERVATION_DONE_FLG === "1") return "予約済み";
  if (schedule.RESERVATION_FLG === "1") return "要予約";
  return "未設定";
}

function shouldShowReservation(schedule) {
  return schedule.NEED_FLG !== "0" && schedule.RESERVATION_FLG === "1";
}

function groupSchedules(schedules) {
  const grouped = new Map();

  (schedules || []).forEach((schedule) => {
    const key = schedule.START_YMD || "日付未設定";
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(schedule);
  });

  return Array.from(grouped.entries()).map(([date, items], index) => ({
    dayNumber: index + 1,
    date,
    items,
  }));
}

function getScheduleIcon(schedule) {
  const text = [
    schedule.CATEGORY_CDV,
    schedule.TRANSPORTATION_NMV,
    schedule.SUBJECT_NMV,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (/飛行機|航空|空港|plane|flight/.test(text)) {
    return '<svg viewBox="0 0 24 24"><path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5L21 16Z"/></svg>';
  }
  if (/食事|グルメ|レストラン|カフェ|food|meal/.test(text)) {
    return '<svg viewBox="0 0 24 24"><path d="M7 2h2v8a3 3 0 0 1-2 2.82V22H5v-9.18A3 3 0 0 1 3 10V2h2v6h2V2Zm9 0c3 0 5 3 5 7 0 2.4-1.2 4.3-3 4.85V22h-2V2Z"/></svg>';
  }
  if (/宿泊|ホテル|旅館|民宿|hotel|stay/.test(text)) {
    return '<svg viewBox="0 0 24 24"><path d="M3 5h2v9h14V8a3 3 0 0 1 3 3v8h-2v-2H4v2H2V6a1 1 0 0 1 1-1Zm5 2h5a3 3 0 0 1 3 3v2H7V8a1 1 0 0 1 1-1Z"/></svg>';
  }
  if (/電車|鉄道|バス|車|移動|train|bus|car/.test(text)) {
    return '<svg viewBox="0 0 24 24"><path d="M5 3h14a2 2 0 0 1 2 2v11a3 3 0 0 1-3 3l2 2h-3l-2-2H9l-2 2H4l2-2a3 3 0 0 1-3-3V5a2 2 0 0 1 2-2Zm1 3v6h12V6H6Zm1 8a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm10 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"/></svg>';
  }

  return '<svg viewBox="0 0 24 24"><path d="M12 2a7 7 0 0 1 7 7c0 5-7 13-7 13S5 14 5 9a7 7 0 0 1 7-7Zm0 4a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z"/></svg>';
}

function buildCoverPage(shiori, members) {
  const names = members.length
    ? members.map((member) => member.MEMBER_NAME).join("、")
    : "未登録";

  return `
    <section class="pdf-page pdf-cover">
      <div class="pdf-cover-visual">
        ${
          shiori.IMAGE_DATA_URI
            ? `<img src="${shiori.IMAGE_DATA_URI}" alt="旅行のメインビジュアル" />`
            : '<div class="pdf-cover-placeholder"><span>R</span><p>Have a wonderful trip.</p></div>'
        }
        <div class="pdf-cover-shade"></div>
        <div class="pdf-cover-brand">
          <img src="/favicon.png" alt="" />
          <span>RabiiTrip · Travel Book</span>
        </div>
        <div class="pdf-cover-content">
          <p class="pdf-cover-kicker">${escapeHtml(
            parseDestinations(shiori),
          )}</p>
          <h1>${escapeHtml(shiori.SUBJECT_NMV || "旅のしおり")}</h1>
          <div class="pdf-cover-date">${escapeHtml(
            `${formatDate(shiori.TRIP_FROM)} － ${formatDate(shiori.TRIP_TO)}`,
          )}</div>
        </div>
      </div>
      <div class="pdf-cover-footer pdf-keep">
        <div>
          <span class="pdf-label">MEMBERS</span>
          <strong>${escapeHtml(names)}</strong>
        </div>
        <div>
          <span class="pdf-label">ESTIMATED COST</span>
          <strong>${escapeHtml(formatTotalCost(shiori.TOTAL_COST))}</strong>
        </div>
        <small>Generated ${escapeHtml(formatGeneratedAt())}</small>
      </div>
    </section>
  `;
}

function buildScheduleDetail(schedule) {
  const address = buildAddress(schedule);
  const phone = buildPhone(schedule);
  const mapUrl = buildMapUrl(schedule);
  const cost =
    Number(schedule.COST || 0) > 0
      ? formatMoney(schedule.COST, schedule.CURRENCY || "JPY")
      : "";

  const details = [
    address && ["住所", address],
    schedule.RESERVATION_NAME && ["予約名・番号", schedule.RESERVATION_NAME],
    phone && ["電話", phone],
    schedule.TRANSPORTATION_NMV && ["移動", schedule.TRANSPORTATION_NMV],
    shouldShowReservation(schedule) && ["予約状況", getReservationText(schedule)],
    cost && ["費用", cost],
  ].filter(Boolean);

  return `
    <article class="pdf-timeline-item pdf-keep">
      <div class="pdf-timeline-time">${escapeHtml(
        schedule.START_TIME || "--:--",
      )}${
        schedule.END_TIME
          ? `<span>${escapeHtml(schedule.END_TIME)}まで</span>`
          : ""
      }</div>
      <div class="pdf-timeline-marker">${getScheduleIcon(schedule)}</div>
      <div class="pdf-timeline-card">
        <div class="pdf-card-heading">
          <h3>${escapeHtml(schedule.SUBJECT_NMV || "予定")}</h3>
          ${
            shouldShowReservation(schedule)
              ? `<span>${escapeHtml(getReservationText(schedule))}</span>`
              : ""
          }
        </div>
        ${
          details.length
            ? `<dl class="pdf-detail-grid">${details
                .map(
                  ([label, value]) =>
                    `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(
                      value,
                    )}</dd></div>`,
                )
                .join("")}</dl>`
            : ""
        }
        ${
          schedule.URL_NMV
            ? `<a class="pdf-link" href="${escapeHtml(
                schedule.URL_NMV,
              )}">Webサイトを開く</a>`
            : ""
        }
        ${
          mapUrl
            ? `<a class="pdf-map" href="${escapeHtml(mapUrl)}">
                <span class="pdf-map-pin">＋</span>
                <span><strong>ミニマップ</strong><small>Google Mapsで場所を確認</small></span>
              </a>`
            : ""
        }
        ${
          schedule.REMARKS
            ? `<div class="pdf-schedule-note">${escapeHtml(
                schedule.REMARKS,
              )}</div>`
            : ""
        }
      </div>
    </article>
  `;
}

function buildSchedulePages(schedules) {
  const days = groupSchedules(schedules);
  if (!days.length) {
    return `
      <section class="pdf-page">
        ${buildPageHeading("Schedule", "スケジュール")}
        <div class="pdf-empty">スケジュールは登録されていません。</div>
      </section>
    `;
  }

  return days
    .map(
      (day) => `
        <section class="pdf-page pdf-schedule-page">
          <header class="pdf-day-heading pdf-keep">
            <div class="pdf-day-tab">DAY ${day.dayNumber}</div>
            <div>
              <p>ITINERARY</p>
              <h2>${escapeHtml(formatDate(day.date))}</h2>
            </div>
          </header>
          <div class="pdf-timeline">
            ${day.items.map(buildScheduleDetail).join("")}
          </div>
        </section>
      `,
    )
    .join("");
}

function classifyChecklistItem(item) {
  const text = `${item.CATEGORY_NAME || ""} ${item.ITEM_NAME || ""}`;
  return (
    checklistGroups.find(
      (group) =>
        group.key !== "other" &&
        group.keywords.some((keyword) => text.includes(keyword)),
    ) || checklistGroups[checklistGroups.length - 1]
  );
}

function buildChecklistItems(items) {
  if (!items.length) {
    return '<p class="pdf-empty-small">項目はありません</p>';
  }

  return items
    .map(
      (item) => `
        <div class="pdf-check-item pdf-keep">
          <span class="pdf-checkbox ${
            item.COMPLETED_FLG === "1" ? "is-checked" : ""
          }">${item.COMPLETED_FLG === "1" ? "✓" : ""}</span>
          <span>${escapeHtml(item.ITEM_NAME || "項目")}</span>
        </div>
      `,
    )
    .join("");
}

function buildChecklistPage(checklists) {
  const belongings = (checklists || []).filter(
    (item) => item.ITEM_TYPE === "BELONGINGS",
  );
  const todos = (checklists || []).filter((item) => item.ITEM_TYPE === "TODO");
  const grouped = Object.fromEntries(
    checklistGroups.map((group) => [group.key, []]),
  );

  belongings.forEach((item) => {
    grouped[classifyChecklistItem(item).key].push(item);
  });

  return `
    <section class="pdf-page">
      ${buildPageHeading(
        "Packing list",
        "持ち物・チェックリスト",
        "忘れ物がないか、出発前にひとつずつ確認しましょう。",
      )}
      <div class="pdf-check-grid">
        ${checklistGroups
          .map(
            (group) => `
              <section class="pdf-check-group pdf-keep">
                <h3><span></span>${group.label}</h3>
                ${buildChecklistItems(grouped[group.key])}
              </section>
            `,
          )
          .join("")}
      </div>
      ${
        todos.length
          ? `<section class="pdf-todo-section">
              <h3>旅行前 To Do</h3>
              <div class="pdf-todo-grid">${buildChecklistItems(todos)}</div>
            </section>`
          : ""
      }
    </section>
  `;
}

function isAccommodation(schedule) {
  const text = `${schedule.CATEGORY_CDV || ""} ${
    schedule.SUBJECT_NMV || ""
  }`.toLowerCase();
  return /宿泊|ホテル|旅館|民宿|hotel|stay/.test(text);
}

function buildContactPage(schedules, notebooks) {
  const accommodations = (schedules || []).filter(isAccommodation);
  const notes = notebooks || [];

  return `
    <section class="pdf-page">
      ${buildPageHeading(
        "Notes & contacts",
        "旅のメモ・連絡先",
        "宿泊先や緊急時に必要な情報を、すぐ見つけられる場所に。",
      )}
      <section class="pdf-contact-section">
        <h3>宿泊先情報</h3>
        ${
          accommodations.length
            ? accommodations
                .map((hotel) => {
                  const mapUrl = buildMapUrl(hotel);
                  return `
                    <article class="pdf-contact-card pdf-keep">
                      <div class="pdf-contact-icon">${getScheduleIcon(
                        hotel,
                      )}</div>
                      <div>
                        <h4>${escapeHtml(hotel.SUBJECT_NMV || "宿泊先")}</h4>
                        <p>${escapeHtml(buildAddress(hotel) || "住所未登録")}</p>
                        <p>${escapeHtml(buildPhone(hotel) || "電話番号未登録")}</p>
                        ${
                          mapUrl
                            ? `<a href="${escapeHtml(mapUrl)}">地図を開く</a>`
                            : ""
                        }
                      </div>
                    </article>
                  `;
                })
                .join("")
            : `<div class="pdf-contact-card pdf-keep">
                <div class="pdf-contact-icon">${getScheduleIcon({
                  CATEGORY_CDV: "宿泊",
                })}</div>
                <div><h4>宿泊先</h4><p>ホテル名：</p><p>住所：</p><p>電話番号：</p></div>
              </div>`
        }
      </section>
      <section class="pdf-notes-section">
        <h3>メモ</h3>
        ${
          notes.length
            ? notes
                .map(
                  (note) => `
                    <div class="pdf-note-card pdf-keep">${escapeHtml(
                      note.NOTE_CONTENT || "",
                    )}</div>
                  `,
                )
                .join("")
            : '<div class="pdf-note-lines" aria-label="自由記入欄"></div>'
        }
      </section>
    </section>
  `;
}

function buildPageHeading(eyebrow, title, description = "") {
  return `
    <header class="pdf-page-heading pdf-keep">
      <p>${escapeHtml(eyebrow)}</p>
      <h2>${escapeHtml(title)}</h2>
      ${description ? `<span>${escapeHtml(description)}</span>` : ""}
    </header>
  `;
}

function buildPdfHtml(payload) {
  const shiori = payload.shiori || {};
  const members = payload.members || [];
  const schedules = payload.schedules || [];

  return `
    <style>
      @page {
        size: A4 portrait;
        margin: 0;
      }

      :root {
        --ink: #18312d;
        --muted: #65736f;
        --accent: #2f7d6b;
        --accent-soft: #e8f2ee;
        --sand: #f6f2ea;
        --line: #dce5e1;
        --paper: #ffffff;
      }

      * {
        box-sizing: border-box;
      }

      html,
      body {
        margin: 0;
        padding: 0;
        background: #eef2f0;
        color: var(--ink);
        font-family: "Noto Sans JP", "Hiragino Sans", "Yu Gothic", Meiryo, sans-serif;
        line-height: 1.65;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      a {
        color: var(--accent);
        text-decoration: none;
      }

      .pdf-screen-toolbar {
        position: sticky;
        top: 0;
        z-index: 20;
        display: flex;
        justify-content: center;
        gap: 12px;
        padding: 12px;
        background: rgba(24, 49, 45, 0.94);
        color: #fff;
        font-size: 13px;
      }

      .pdf-screen-toolbar button {
        border: 0;
        border-radius: 999px;
        padding: 8px 18px;
        background: #fff;
        color: var(--ink);
        font: inherit;
        font-weight: 700;
        cursor: pointer;
      }

      .pdf-document {
        width: 210mm;
        margin: 8mm auto;
        background: var(--paper);
        box-shadow: 0 8px 32px rgba(24, 49, 45, 0.12);
      }

      .pdf-page {
        position: relative;
        width: 210mm;
        min-height: 297mm;
        padding: 18mm;
        background: var(--paper);
        break-after: page;
        page-break-after: always;
      }

      .pdf-page:last-child {
        break-after: auto;
        page-break-after: auto;
      }

      .pdf-keep,
      .pdf-timeline-item,
      .pdf-check-item,
      .pdf-contact-card,
      .pdf-note-card {
        break-inside: avoid;
        page-break-inside: avoid;
      }

      .pdf-cover {
        height: 297mm;
        padding: 0;
        overflow: hidden;
        display: grid;
        grid-template-rows: 224mm 73mm;
      }

      .pdf-cover-visual {
        position: relative;
        overflow: hidden;
        background: linear-gradient(145deg, #89a99d, #315f55);
      }

      .pdf-cover-visual > img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }

      .pdf-cover-placeholder {
        width: 100%;
        height: 100%;
        display: grid;
        place-content: center;
        justify-items: center;
        color: rgba(255, 255, 255, 0.9);
        background:
          radial-gradient(circle at 78% 20%, rgba(255,255,255,.22), transparent 30%),
          linear-gradient(145deg, #8fb4a8, #315f55);
      }

      .pdf-cover-placeholder span {
        display: grid;
        place-items: center;
        width: 28mm;
        height: 28mm;
        border: 1px solid rgba(255,255,255,.7);
        border-radius: 50%;
        font-family: Georgia, serif;
        font-size: 38px;
      }

      .pdf-cover-placeholder p {
        letter-spacing: .2em;
        text-transform: uppercase;
      }

      .pdf-cover-shade {
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, rgba(8,30,27,.08), rgba(8,30,27,.72));
      }

      .pdf-cover-brand {
        position: absolute;
        top: 13mm;
        left: 16mm;
        display: inline-flex;
        align-items: center;
        gap: 2mm;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: .04em;
      }

      .pdf-cover-brand img {
        width: 7mm;
        height: 7mm;
        object-fit: contain;
      }

      .pdf-cover-content {
        position: absolute;
        right: 16mm;
        bottom: 17mm;
        left: 16mm;
        color: #fff;
      }

      .pdf-cover-kicker {
        margin: 0 0 4mm;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: .12em;
      }

      .pdf-cover-content h1 {
        max-width: 165mm;
        margin: 0 0 6mm;
        font-family: "Yu Mincho", "Hiragino Mincho ProN", serif;
        font-size: 34px;
        line-height: 1.35;
        letter-spacing: .04em;
      }

      .pdf-cover-date {
        display: inline-block;
        padding-top: 4mm;
        border-top: 1px solid rgba(255,255,255,.55);
        font-size: 13px;
      }

      .pdf-cover-footer {
        display: grid;
        grid-template-columns: 1.4fr 1fr;
        gap: 8mm;
        align-content: center;
        padding: 12mm 16mm;
        background: var(--sand);
      }

      .pdf-cover-footer > div {
        display: grid;
        gap: 1mm;
      }

      .pdf-cover-footer small {
        position: absolute;
        right: 16mm;
        bottom: 7mm;
        color: var(--muted);
        font-size: 8px;
      }

      .pdf-label {
        color: var(--muted);
        font-size: 8px;
        font-weight: 700;
        letter-spacing: .16em;
      }

      .pdf-cover-footer strong {
        font-size: 13px;
      }

      .pdf-page-heading {
        margin-bottom: 12mm;
        padding-bottom: 6mm;
        border-bottom: 1px solid var(--line);
      }

      .pdf-page-heading p,
      .pdf-day-heading p {
        margin: 0 0 1mm;
        color: var(--accent);
        font-size: 9px;
        font-weight: 800;
        letter-spacing: .18em;
        text-transform: uppercase;
      }

      .pdf-page-heading h2,
      .pdf-day-heading h2 {
        margin: 0;
        font-family: "Yu Mincho", "Hiragino Mincho ProN", serif;
        font-size: 25px;
        font-weight: 700;
        letter-spacing: .04em;
      }

      .pdf-page-heading span {
        display: block;
        margin-top: 2mm;
        color: var(--muted);
        font-size: 11px;
      }

      .pdf-day-heading {
        display: flex;
        align-items: center;
        gap: 6mm;
        margin-bottom: 10mm;
        padding-bottom: 6mm;
        border-bottom: 1px solid var(--line);
      }

      .pdf-day-tab {
        display: grid;
        place-items: center;
        width: 25mm;
        height: 25mm;
        border-radius: 7mm;
        background: var(--accent);
        color: #fff;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: .08em;
      }

      .pdf-timeline {
        position: relative;
      }

      .pdf-timeline::before {
        content: "";
        position: absolute;
        top: 7mm;
        bottom: 7mm;
        left: 32mm;
        width: 1px;
        background: var(--line);
      }

      .pdf-timeline-item {
        position: relative;
        display: grid;
        grid-template-columns: 25mm 12mm 1fr;
        gap: 3.5mm;
        margin-bottom: 6mm;
      }

      .pdf-timeline-time {
        padding-top: 2mm;
        font-size: 13px;
        font-weight: 800;
        text-align: right;
      }

      .pdf-timeline-time span {
        display: block;
        color: var(--muted);
        font-size: 8px;
        font-weight: 500;
      }

      .pdf-timeline-marker,
      .pdf-contact-icon {
        z-index: 1;
        display: grid;
        place-items: center;
        width: 10mm;
        height: 10mm;
        border-radius: 50%;
        background: var(--accent-soft);
        color: var(--accent);
      }

      .pdf-timeline-marker svg,
      .pdf-contact-icon svg {
        width: 5mm;
        height: 5mm;
        fill: currentColor;
      }

      .pdf-timeline-card {
        padding: 5mm;
        border: 1px solid var(--line);
        border-radius: 4mm;
        background: #fff;
      }

      .pdf-card-heading {
        display: flex;
        justify-content: space-between;
        gap: 4mm;
        align-items: flex-start;
        margin-bottom: 3mm;
      }

      .pdf-card-heading h3 {
        margin: 0;
        font-size: 15px;
        line-height: 1.45;
      }

      .pdf-card-heading > span {
        flex: 0 0 auto;
        padding: 1mm 2.5mm;
        border-radius: 999px;
        background: var(--accent-soft);
        color: var(--accent);
        font-size: 8px;
        font-weight: 700;
      }

      .pdf-detail-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 2mm 5mm;
        margin: 0;
      }

      .pdf-detail-grid div {
        min-width: 0;
      }

      .pdf-detail-grid dt {
        color: var(--muted);
        font-size: 8px;
        font-weight: 700;
      }

      .pdf-detail-grid dd {
        margin: 0;
        font-size: 10px;
        overflow-wrap: anywhere;
      }

      .pdf-link {
        display: inline-block;
        margin-top: 3mm;
        font-size: 9px;
        font-weight: 700;
      }

      .pdf-map {
        display: flex;
        align-items: center;
        gap: 3mm;
        min-height: 17mm;
        margin-top: 3mm;
        padding: 3mm;
        border-radius: 3mm;
        background:
          linear-gradient(45deg, transparent 46%, rgba(47,125,107,.08) 47%, rgba(47,125,107,.08) 53%, transparent 54%),
          #f5f8f7;
        background-size: 10mm 10mm;
      }

      .pdf-map-pin {
        display: grid;
        place-items: center;
        width: 9mm;
        height: 9mm;
        border-radius: 50%;
        background: var(--accent);
        color: #fff;
        font-size: 15px;
      }

      .pdf-map strong,
      .pdf-map small {
        display: block;
      }

      .pdf-map strong {
        font-size: 9px;
      }

      .pdf-map small {
        color: var(--muted);
        font-size: 8px;
      }

      .pdf-schedule-note {
        margin-top: 3mm;
        padding: 3mm;
        border-left: 2px solid #d8c9aa;
        background: var(--sand);
        color: #5f5a50;
        font-size: 9px;
      }

      .pdf-check-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 6mm;
      }

      .pdf-check-group {
        min-height: 51mm;
        padding: 5mm;
        border: 1px solid var(--line);
        border-radius: 4mm;
      }

      .pdf-check-group h3,
      .pdf-todo-section h3,
      .pdf-contact-section > h3,
      .pdf-notes-section > h3 {
        margin: 0 0 4mm;
        font-size: 13px;
      }

      .pdf-check-group h3 {
        display: flex;
        align-items: center;
        gap: 2mm;
      }

      .pdf-check-group h3 span {
        width: 2mm;
        height: 5mm;
        border-radius: 999px;
        background: var(--accent);
      }

      .pdf-check-item {
        display: flex;
        align-items: flex-start;
        gap: 2.5mm;
        padding: 2mm 0;
        border-bottom: 1px dotted var(--line);
        font-size: 10px;
      }

      .pdf-checkbox {
        flex: 0 0 auto;
        display: grid;
        place-items: center;
        width: 4.5mm;
        height: 4.5mm;
        margin-top: .5mm;
        border: 1px solid #aebbb6;
        border-radius: 1mm;
        color: #fff;
        font-size: 9px;
        line-height: 1;
      }

      .pdf-checkbox.is-checked {
        border-color: var(--accent);
        background: var(--accent);
      }

      .pdf-empty-small,
      .pdf-empty {
        color: var(--muted);
        font-size: 10px;
      }

      .pdf-empty {
        padding: 12mm;
        border-radius: 4mm;
        background: var(--sand);
        text-align: center;
      }

      .pdf-todo-section {
        margin-top: 8mm;
        padding: 5mm;
        border-radius: 4mm;
        background: var(--sand);
      }

      .pdf-todo-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0 7mm;
      }

      .pdf-contact-section {
        margin-bottom: 10mm;
      }

      .pdf-contact-card {
        display: grid;
        grid-template-columns: 12mm 1fr;
        gap: 4mm;
        margin-bottom: 4mm;
        padding: 5mm;
        border: 1px solid var(--line);
        border-radius: 4mm;
      }

      .pdf-contact-card h4 {
        margin: 0 0 2mm;
        font-size: 14px;
      }

      .pdf-contact-card p {
        margin: 1mm 0;
        color: var(--muted);
        font-size: 10px;
      }

      .pdf-contact-card a {
        display: inline-block;
        margin-top: 2mm;
        font-size: 9px;
        font-weight: 700;
      }

      .pdf-note-card {
        min-height: 24mm;
        margin-bottom: 4mm;
        padding: 5mm;
        border-radius: 4mm;
        background: var(--sand);
        font-size: 10px;
        white-space: normal;
      }

      .pdf-note-lines {
        height: 83mm;
        background: repeating-linear-gradient(
          to bottom,
          transparent 0,
          transparent 11mm,
          var(--line) 11.2mm,
          var(--line) 11.5mm
        );
      }

      @media screen and (max-width: 900px) {
        .pdf-document {
          margin: 0;
          transform-origin: top left;
        }
      }

      @media print {
        html,
        body {
          width: 210mm;
          background: #fff;
        }

        .pdf-screen-toolbar,
        button,
        .no-print {
          display: none !important;
        }

        .pdf-document {
          width: 210mm;
          margin: 0;
          box-shadow: none;
        }

        .pdf-page {
          margin: 0;
          break-after: page;
          page-break-after: always;
        }

        .pdf-page-heading,
        .pdf-day-heading,
        h1,
        h2,
        h3,
        h4,
        p,
        dl,
        a,
        .pdf-timeline-card,
        .pdf-check-group,
        .pdf-contact-card,
        .pdf-note-card {
          orphans: 3;
          widows: 3;
        }

        .pdf-keep,
        .pdf-timeline-item,
        .pdf-timeline-card,
        .pdf-check-group,
        .pdf-check-item,
        .pdf-contact-card,
        .pdf-note-card {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }
      }
    </style>
    <div class="pdf-screen-toolbar no-print">
      <span>印刷ダイアログで「PDFに保存」を選択してください</span>
      <button type="button" onclick="window.print()">印刷 / PDF保存</button>
    </div>
    <main class="pdf-document">
      ${buildCoverPage(shiori, members)}
      ${buildSchedulePages(schedules)}
      ${buildChecklistPage(payload.checklists || [])}
      ${buildContactPage(schedules, payload.notebooks || [])}
    </main>
  `;
}

function formatFileDate(value) {
  const date = value ? new Date(value) : new Date();

  if (Number.isNaN(date.getTime())) {
    const fallback = String(value || "").replace(/[^0-9]/g, "");
    return fallback.slice(0, 8) || formatFileDate();
  }

  return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}${String(date.getDate()).padStart(2, "0")}`;
}

function buildFileName(title, tripStartDate) {
  const safeTitle = String(title || "shiori")
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 60);

  return `${safeTitle || "shiori"}_${formatFileDate(tripStartDate)}.pdf`;
}

function buildLoadingHtml(title) {
  return `<!doctype html>
  <html lang="ja">
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <link rel="icon" href="/favicon.png" />
      <title>${escapeHtml(title)}</title>
      <style>
        html, body {
          margin: 0;
          min-height: 100%;
          font-family: "Hiragino Sans", "Yu Gothic", Meiryo, sans-serif;
          background: #f3f6f5;
          color: #18312d;
        }
        body { display: grid; place-items: center; }
        .loading-card {
          padding: 24px 28px;
          border: 1px solid #dce5e1;
          border-radius: 16px;
          background: #fff;
          box-shadow: 0 12px 30px rgba(24, 49, 45, .08);
        }
      </style>
    </head>
    <body><div class="loading-card">PDFを準備しています...</div></body>
  </html>`;
}

function buildPrintDocumentHtml(payload, title) {
  return `<!doctype html>
  <html lang="ja">
    <head>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <title>${escapeHtml(title)}</title>
    </head>
    <body>
      ${buildPdfHtml(payload)}
      <script>
        window.addEventListener("afterprint", function () {
          window.close();
        });
      </script>
    </body>
  </html>`;
}

export function openShioriPdfWindow(title = "PDF保存") {
  const printWindow = window.open("", "_blank");

  if (!printWindow) {
    throw new Error(
      "PDF保存用のウィンドウを開けませんでした。ポップアップブロックを確認してください。",
    );
  }

  printWindow.document.open();
  printWindow.document.write(buildLoadingHtml(title));
  printWindow.document.close();
  return printWindow;
}

async function waitForPrintWindowReady(printWindow) {
  await new Promise((resolve) => {
    printWindow.setTimeout(resolve, 300);
  });

  if (printWindow.document?.fonts?.ready) {
    try {
      await printWindow.document.fonts.ready;
    } catch {
      // フォント待機に失敗しても印刷は続行する。
    }
  }

  const images = Array.from(printWindow.document?.images || []);
  await Promise.all(
    images.map((image) => {
      if (image.complete) return Promise.resolve();
      return new Promise((resolve) => {
        image.addEventListener("load", resolve, { once: true });
        image.addEventListener("error", resolve, { once: true });
      });
    }),
  );
}

export async function exportShioriPdf(payload, printWindow = null) {
  if (!payload?.shiori) {
    throw new Error("PDF出力データが見つかりません");
  }

  const filename = buildFileName(
    payload.shiori.SUBJECT_NMV,
    payload.shiori.TRIP_FROM,
  );
  const windowTitle = filename.replace(/\.pdf$/i, "");
  const targetWindow = printWindow || openShioriPdfWindow(windowTitle);

  try {
    targetWindow.document.open();
    targetWindow.document.write(buildPrintDocumentHtml(payload, windowTitle));
    targetWindow.document.close();

    await waitForPrintWindowReady(targetWindow);
    targetWindow.focus();
    targetWindow.print();
    return { method: "print", filename };
  } catch (error) {
    if (!targetWindow.closed) targetWindow.close();
    throw error;
  }
}
